---
title: "Do You Know Which Legacy Lines Still Support Your Buildings?"
keywords: "POTS replacement, copper retirement, Plain Old Telephone Service, analog line replacement, legacy phone lines, carrier discontinuance, legacy voice services, fire alarm phone lines, elevator phone lines, fax line replacement"
seo:
  title: "Legacy Line Risk Review | POTS Replacement & Copper Retirement"
  description: "Fire panels, elevator phones, alarms, and fax lines often still depend on aging analog voice services. Request a Legacy Line Risk Review to identify hidden costs, compliance gaps, and continuity risks before they become a problem."
  image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=1200"
  twitter:
    card: "summary_large_image"
    title: "Legacy Line Risk Review | POTS Replacement & Copper Retirement"
    description: "Identify hidden analog line dependencies across your buildings. Request a Legacy Line Risk Review from Gage Technologies."
    image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=1200"
---

<PotsAlertBar>
  <strong>Federal action underway:</strong> The FCC has authorized carriers to retire legacy copper networks. Carriers are issuing discontinuance notices. <a href="#contact">Assess your exposure →</a>
</PotsAlertBar>

<PotsHero
  title="Do You Know Which Legacy Lines Still Support Your Buildings?"
  description="Fire panels, elevator phones, alarms, gates, fax lines, call boxes, modems, and emergency phones often still depend on aging analog voice services. As carriers raise costs, issue discontinuance notices, and move away from legacy copper infrastructure, hidden lines can become a cost, compliance, and continuity risk."
  ctaText="Request a Legacy Line Risk Review"
  ctaHref="#contact"
  secondaryCtaText="Start With One Site or One Bill"
  secondaryCtaHref="#contact"
/>

<Section>
  <PotsEyebrow>The Problem</PotsEyebrow>
  ## Where Legacy Lines Usually Hide

  Most organizations don't have a clean inventory of every analog line still in use. Many are tied to systems that live outside normal IT ownership.

  <PotsCards>
    <PotsCard
      icon="flame"
      title="Life Safety"
      description="Fire panels, elevator phones, and emergency phones that must stay connected for code compliance and occupant safety."
    />
    <PotsCard
      icon="lock"
      title="Security & Access"
      description="Gates, alarms, call boxes, and access control systems that rely on analog lines for monitoring and alerts."
    />
    <PotsCard
      icon="file"
      title="Business Operations"
      description="Fax machines, modems, metered lines, and backup circuits still in active use across departments."
    />
    <PotsCard
      icon="search"
      title="Unknown Charges"
      description="Old carrier bills with duplicate lines, unused circuits, and services no one actively manages or remembers."
    />
  </PotsCards>

  <div class="pots-inline-cta">
    <ButtonCTA href="#contact" class="themed">Identify Hidden Analog Line Dependencies</ButtonCTA>
  </div>
</Section>

<Section data-gray>
  <PotsEyebrow>Why Now</PotsEyebrow>
  ## Why This Matters Now

  <PotsCards>
    <PotsCard
      icon="radio"
      title="Carriers Are Retiring Copper"
      description="Plain Old Telephone Service and legacy copper infrastructure are being phased out. Carrier discontinuance notices are already going out in many regions."
    />
    <PotsCard
      icon="trending"
      title="Costs Are Rising"
      description="As carriers wind down legacy voice services, monthly costs for aging analog lines are climbing, often without anyone noticing the increase."
    />
    <PotsCard
      icon="warning"
      title="Critical Systems at Risk"
      description="Lines no one actively owns may still support fire alarms, elevator phones, or security systems. The gap shows up when something stops working."
    />
  </PotsCards>

  <PotsCallout variant="accent">
    <p><strong>The risk is finding out too late.</strong> A line gets discontinued, and only then does anyone check what it was supporting.</p>
  </PotsCallout>
</Section>

<Section>
  <PotsEyebrow>The Offer</PotsEyebrow>
  ## What Gage Helps You Do

  <PotsFramework framework="Inventory. Prioritize. Replace or Retire.">
    - Identify legacy analog lines across sites, buildings, and carrier bills
    - Understand what each line supports and why it exists
    - Flag cost, continuity, and compliance risk for each dependency
    - Map practical replacement or retirement options
    - Build a prioritized site-by-site action plan
  </PotsFramework>

  <div class="pots-inline-cta">
    <ButtonCTA href="#contact" class="themed">Start a Review</ButtonCTA>
  </div>
</Section>

<Section data-gray>
  <PotsEyebrow>Get Started</PotsEyebrow>
  ## Start With One Site, One Bill, or One Group of Lines

  You don't need a perfect inventory to begin. Many organizations start with one telecom bill, one building, one site, or one known group of analog lines. Gage can help identify likely legacy services, high-cost charges, and areas that need a closer look.

  <CTAButtons>
    <ButtonCTA href="#contact" class="themed">Start a Review</ButtonCTA>
  </CTAButtons>
</Section>

<Section>
  <PotsEyebrow>Who This Is For</PotsEyebrow>
  ## Who Should Care

  <PotsCards>
    <PotsCard
      icon="monitor"
      title="IT"
      description="Carrier changes, voice dependencies, network readiness, backup connectivity, and vendor coordination."
    />
    <PotsCard
      icon="building"
      title="Facilities"
      description="Elevator phones, fire panels, alarms, inspections, and building continuity across every site."
    />
    <PotsCard
      icon="dollar"
      title="Finance & Procurement"
      description="Rising line costs, duplicate charges, unused services, and telecom contract cleanup."
    />
    <PotsCard
      icon="shield"
      title="Risk, Compliance & Operations"
      description="Emergency communications, life-safety systems, documentation, and continuity planning."
    />
  </PotsCards>
</Section>

<Section data-gray>
  <PotsEyebrow>Why Gage</PotsEyebrow>
  ## Why Gage

  Legacy line replacement is rarely just a telecom issue. It can touch facilities, compliance, carrier services, network infrastructure, voice, monitoring, backup connectivity, and long-term support. Gage brings those pieces together so legacy line cleanup does not fall between IT, facilities, and carriers.

  <PotsCallout>
    <p><strong>Practical, not product-led.</strong> We start with what you have, not with a product to sell. POTS replacement, copper retirement, and analog line replacement are part of the conversation, but the review comes first.</p>
  </PotsCallout>
</Section>

<Section id="contact">
  <ContactForm>
    ## Request a Legacy<br/>Line Risk Review

    <p class="pots-contact-subcopy" style="text-align: center; margin-left: auto; margin-right: auto;">If you are not sure which analog lines still support your buildings, systems, or sites, Gage can help you start with a practical review.</p>
  </ContactForm>
</Section>
